// ── Welle D3c: Widgets aus der Ablage — neu (Angepinnte Notizen, Ausflüge & Aktionen,
//    Stundenplan Woche, Offene Klassenbuch-Einträge) und die früheren Heute-Karten
//    (Klassenarbeiten, Eltern-Rollen, Wiederkehrend fehlend, Klassenbuch-Aktivität,
//    Ausstehende Aktionen) im neuen Look. Anzahl der Zeilen nach Kachelhöhe.
import React from 'react';
import { MIcon } from '../../../lib/micon';
import { apiFetch, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { confirm } from '../../../lib/confirm';
import { useSchueler } from '../../../contexts/SchuelerContext';
import {
  globalNav, oeffneKlassenAktionen, oeffneNotizen, oeffneTerminBearbeiten, openAusflugDetail,
} from '../../../lib/nav';
import { useKlassenbuchSprung } from './sprung';
import { useZeitraster, fmtZeit } from '../../../lib/zeitraster';
import { katInfo, notizText, Notiz } from '../../notizen/notizen-lib';
import { MitDaten, ladeNeu, useLader } from './lader';
import { useWidgetGroesse, WidgetGroesse } from './groesse';

// Zeilen je Kachelhöhe: 1 Rasterzeile ≈ 2 Listenzeilen (plus 1 je weiterer Lücke zwischen den Zeilen)
const zeilen = (g: WidgetGroesse, proZeile = 2) => g.geraet === 'pc' ? g.h * proZeile + (g.h - 1) : g.w >= 2 ? proZeile * 2 : proZeile;
const ttmm = (d: string) => { const x = new Date(d.slice(0, 10) + 'T12:00:00'); return `${x.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')} ${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.`; };

function Leer({ icon, text, aktion }: { icon: string; text: string; aktion?: { label: string; ziel: () => void } }) {
  return (
    <div className="ld-wleer"><MIcon name={icon} size={22} /><span>{text}</span>
      {aktion && <button type="button" className="ld-wlink" onClick={aktion.ziel}>{aktion.label}</button>}
    </div>
  );
}
function Mehr({ n, gezeigt, label, ziel }: { n: number; gezeigt: number; label: string; ziel: () => void }) {
  return n > gezeigt ? <button type="button" className="ld-wmehr" onClick={ziel}>{label.replace('{n}', String(n))}</button> : null;
}

// ── Angepinnte Notizen ──────────────────────────────────────────────────────
export function NotizenWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<{ notizen: (Notiz & { schueler_name?: string; klasse_name?: string; klasse_id?: number })[]; gesamt: number }>('/api/notizen?angepinnt=1&limit=20');
  const n = gr.geraet === 'pc' ? gr.h * 2 * (gr.w >= 2 ? 2 : 1) : gr.w >= 2 ? 4 : 2;
  return (
    <MitDaten g={g}>
      {({ notizen, gesamt }) => notizen.length === 0 ? (
        <Leer icon="push_pin" text="Noch nichts angepinnt. Wichtige Notizen pinnst du in der Notiz an." aktion={{ label: 'Zu den Notizen', ziel: () => globalNav('notizen') }} />
      ) : (
        <div className="ld-wspalte">
          <div className={`ld-pins${gr.geraet === 'pc' && gr.w >= 2 ? ' ld-pins--zwei' : ''}`}>
            {notizen.slice(0, n).map(nz => {
              const k = katInfo(nz.kategorie);
              return (
                <button key={nz.id} type="button" className="ld-pin" onClick={() => oeffneNotizen(nz.schueler_id, null, nz.id)}>
                  <span className="ld-pin__kopf"><MIcon name="push_pin" size={14} fill /><b>{nz.schueler_name ?? 'Notiz'}</b>
                    <span className="ld-pin__kat" style={{ background: k.bg, color: k.ink }}>{k.label}</span></span>
                  <span className="ld-pin__text">{notizText(nz)}</span>
                </button>
              );
            })}
          </div>
          <Mehr n={gesamt} gezeigt={Math.min(n, notizen.length)} label="Alle {n} angepinnten" ziel={() => globalNav('notizen')} />
        </div>
      )}
    </MitDaten>
  );
}

// ── Ausflüge & Aktionen ─────────────────────────────────────────────────────
type Ausflug = { termin_id: number; titel: string; datum: string; klasse_name: string | null; abgeschlossen_am?: string | null; stats: { gesamt: number; offen_zustimmung: number; offen_zahlung: number } };
type Aktion = { id: number; klasse_id: number; titel: string; faellig_am: string; klasse_name: string; erledigt_anzahl: number; gesamt_anzahl: number };

export function AusfluegeWidget() {
  const gr = useWidgetGroesse();
  const au = useLader<{ aktive_ausfluege: Ausflug[] }>('/api/ausfluege/aktiv');
  const ak = useLader<Aktion[]>('/api/aktionen/faellig');
  const n = zeilen(gr);
  const heute = new Date().toLocaleDateString('sv-SE');
  async function abschliessen(a: Ausflug) {
    if (!(await confirm('Es werden keine Fehlzeiten angelegt.', { title: 'Ausflug abschließen?', confirmLabel: 'Abschließen' }))) return;
    try { await apiFetch(`/api/ausfluege/${a.termin_id}/abschliessen`, { method: 'POST' }); toast('Ausflug abgeschlossen'); void ladeNeu('/api/ausfluege/aktiv'); }
    catch (e) { zeigeApiFehler(e, 'Abschließen fehlgeschlagen'); }
  }
  return (
    <MitDaten g={au}>
      {({ aktive_ausfluege }) => {
        const aktionen = ak.daten ?? [];
        if (!aktive_ausfluege.length && !aktionen.length) return <Leer icon="directions_bus" text="Keine laufenden Ausflüge oder fälligen Aktionen." aktion={{ label: 'Ausflug planen', ziel: () => globalNav('kalender') }} />;
        const eintraege = [
          ...aktive_ausfluege.map(a => ({ typ: 'ausflug' as const, a })),
          ...aktionen.map(x => ({ typ: 'aktion' as const, x })),
        ].slice(0, n);
        return (
          <ul className="ld-wl">
            {eintraege.map(e => e.typ === 'ausflug' ? (
              <li key={`a${e.a.termin_id}`} className="ld-wzeile-mit">
                <button type="button" className="ld-wzeile" onClick={() => openAusflugDetail(e.a.termin_id, 'home')}>
                  <span className="ld-wzeile__icon"><MIcon name="directions_bus" size={16} /></span>
                  <span className="ld-wzeile__text"><b>{e.a.titel}</b>
                    {/* Welle K6: am PC Offenes als eigene Amber-Zeile */}
                    {gr.geraet === 'pc' ? <>
                      <small>{ttmm(e.a.datum)}{e.a.klasse_name ? `, Klasse ${e.a.klasse_name}` : ''}</small>
                      {(e.a.stats.offen_zustimmung > 0 || e.a.stats.offen_zahlung > 0) && (
                        <small className="ld-wamber">{[e.a.stats.offen_zustimmung > 0 ? `${e.a.stats.offen_zustimmung} Rückmeldungen offen` : '', e.a.stats.offen_zahlung > 0 ? `${e.a.stats.offen_zahlung} Zahlungen offen` : ''].filter(Boolean).join(', ')}</small>
                      )}
                    </> :
                    <small>{ttmm(e.a.datum)}{e.a.klasse_name ? ` · ${e.a.klasse_name}` : ''}
                      {e.a.stats.offen_zustimmung > 0 ? ` · ${e.a.stats.offen_zustimmung} Rückmeldungen offen` : ''}
                      {e.a.stats.offen_zahlung > 0 ? ` · ${e.a.stats.offen_zahlung} Zahlungen offen` : ''}
                      {e.a.stats.offen_zustimmung === 0 && e.a.stats.offen_zahlung === 0 ? ' · alles beisammen' : ''}</small>}</span>
                </button>
                {e.a.datum.slice(0, 10) < heute && !e.a.abgeschlossen_am && (
                  <button type="button" className="ld-wknopf" onClick={() => abschliessen(e.a)}>Abschließen</button>
                )}
              </li>
            ) : (
              <li key={`k${e.x.id}`}>
                <button type="button" className="ld-wzeile" onClick={() => oeffneKlassenAktionen(e.x.klasse_id, e.x.id)}>
                  <span className="ld-wzeile__icon"><MIcon name="checklist" size={16} /></span>
                  <span className="ld-wzeile__text"><b>{e.x.titel}</b><small>{e.x.klasse_name} · fällig {ttmm(e.x.faellig_am)} · {e.x.erledigt_anzahl}/{e.x.gesamt_anzahl} erledigt</small></span>
                </button>
              </li>
            ))}
          </ul>
        );
      }}
    </MitDaten>
  );
}

// ── Stundenplan Woche (nur lesen) ───────────────────────────────────────────
type SpEintrag = { id: number; wochentag: number; stunde: number; fach: string; klasse_id: number | null; klasse_name: string | null; raum: string | null };

export function StundenplanWocheWidget() {
  const g = useLader<SpEintrag[]>('/api/stundenplan');
  const sprung = useKlassenbuchSprung();
  const { zeiten } = useZeitraster();
  return (
    <MitDaten g={g} zeilen={3}>
      {(sp) => {
        if (!sp.length) return <Leer icon="calendar_view_week" text="Noch kein Stundenplan eingetragen." aktion={{ label: 'Stundenplan eintragen', ziel: () => globalNav('stundenplan') }} />;
        const heute = new Date();
        const heuteWt = heute.getDay();
        const montag = new Date(heute); montag.setDate(heute.getDate() - ((heuteWt + 6) % 7));
        const datumFuer = (wt: number) => { const d = new Date(montag); d.setDate(montag.getDate() + wt - 1); return d.toLocaleDateString('sv-SE'); };
        const bis = Math.max(...sp.map(e => e.stunde));
        const von = Math.min(...sp.map(e => e.stunde));
        const STUNDEN = Array.from({ length: bis - von + 1 }, (_, i) => von + i);
        return (
          <div className="ld-spw" role="table" aria-label="Stundenplan dieser Woche">
            <div className="ld-spw__zeile ld-spw__kopf" role="row">
              <span role="columnheader" />
              {['Mo', 'Di', 'Mi', 'Do', 'Fr'].map((t, i) => <span key={t} role="columnheader" className={i + 1 === heuteWt ? 'ist-heute' : ''}>{t}</span>)}
            </div>
            {STUNDEN.map(st => (
              <div key={st} className="ld-spw__zeile" role="row">
                <span role="rowheader" className="ld-spw__nr" title={zeiten[st - 1] ? fmtZeit(zeiten[st - 1]![0]) : undefined}>{st}.</span>
                {[1, 2, 3, 4, 5].map(wt => {
                  const e = sp.find(x => x.wochentag === wt && x.stunde === st);
                  return e ? (
                    <button key={wt} type="button" role="cell" className={`ld-spw__zelle${wt === heuteWt ? ' ist-heute' : ''}`}
                      title={`${e.klasse_name ?? ''} ${e.fach}${e.raum ? ` · ${e.raum}` : ''}`}
                      onClick={() => sprung(datumFuer(wt), st, e.klasse_id)}>
                      {e.klasse_name ?? e.fach}
                    </button>
                  ) : <span key={wt} role="cell" className={`ld-spw__zelle ist-leer${wt === heuteWt ? ' ist-heute' : ''}`} />;
                })}
              </div>
            ))}
          </div>
        );
      }}
    </MitDaten>
  );
}

// ── Offene Klassenbuch-Einträge ─────────────────────────────────────────────
type Offen = { eintraege: { datum: string; stunde_nr: number; klasse_id: number; klasse_name: string; fach: string }[]; gesamt: number };
export const KB_OFFEN = '/api/heute/klassenbuch-offen';

export function KlassenbuchOffenZusatz() {
  const g = useLader<Offen>(KB_OFFEN);
  return g.daten && g.daten.gesamt > 0 ? <span className="ld-w__zahl">{g.daten.gesamt}</span> : null;
}

export function KlassenbuchOffenWidget() {
  const gr = useWidgetGroesse();
  const sprung = useKlassenbuchSprung();
  const g = useLader<Offen>(KB_OFFEN);
  const n = Math.max(1, zeilen(gr) - 1); // die Kopfzeile „n Stunden ohne Eintrag“ braucht eine Listenzeile
  return (
    <MitDaten g={g}>
      {({ eintraege, gesamt }) => gesamt === 0 ? (
        <Leer icon="task_alt" text="Alles eingetragen." />
      ) : (
        <div className="ld-wspalte">
          <p className="ld-wtext"><b>{gesamt} {gesamt === 1 ? 'Stunde' : 'Stunden'}</b> ohne Eintrag</p>
          <ul className="ld-wl">
            {eintraege.slice(0, n).map(e => (
              <li key={`${e.datum}|${e.stunde_nr}|${e.klasse_id}`}>
                <button type="button" className="ld-wzeile ld-wzeile--kompakt" onClick={() => sprung(e.datum, e.stunde_nr, e.klasse_id)}>
                  <span className="ld-wzeile__text"><b>{ttmm(e.datum)} · {e.stunde_nr}. Std</b><small>{e.klasse_name} · {e.fach}</small></span>
                  <MIcon name="edit_note" size={18} className="ld-wzeile__pfeil" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </MitDaten>
  );
}

// ── Klassenarbeiten ─────────────────────────────────────────────────────────
type KA = { id: number; titel: string; datum: string; ka_fach: string | null; klasse_name: string | null };
type KaGruppen = { morgen: KA[]; in_einer_woche: KA[]; gestern: KA[]; sonstige: KA[] };

export function KlassenarbeitenWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<KaGruppen>('/api/klassenarbeiten/aktiv', 60_000);
  const n = zeilen(gr);
  return (
    <MitDaten g={g}>
      {(k) => {
        const liste = [
          ...k.morgen.map(x => ({ x, hinweis: 'steht bald an', ton: 'violett' })),
          ...k.gestern.map(x => ({ x, hinweis: 'Korrektur steht an', ton: 'grau' })),
          ...k.in_einer_woche.map(x => ({ x, hinweis: 'in einer Woche', ton: 'grau' })),
          ...k.sonstige.map(x => ({ x, hinweis: '', ton: 'grau' })),
        ];
        if (!liste.length) return <Leer icon="assignment" text="Keine Klassenarbeiten in Sicht." aktion={{ label: 'Im Kalender eintragen', ziel: () => globalNav('kalender') }} />;
        return (
          <ul className="ld-wl">
            {liste.slice(0, n).map(({ x, hinweis, ton }) => (
              <li key={x.id}>
                <button type="button" className="ld-wzeile ld-wzeile--kompakt" onClick={() => oeffneTerminBearbeiten(x.id)}>
                  <span className={`ld-punkt ld-punkt--${ton}`} aria-hidden="true" />
                  <span className="ld-wzeile__text"><b>{x.titel}{x.klasse_name ? ` · ${x.klasse_name}` : ''}</b><small>{ttmm(x.datum)}{x.ka_fach ? ` · ${x.ka_fach}` : ''}{hinweis ? ` · ${hinweis}` : ''}</small></span>
                </button>
              </li>
            ))}
          </ul>
        );
      }}
    </MitDaten>
  );
}

// ── Eltern-Rollen unklar ────────────────────────────────────────────────────
export function ElternZuordnungWidget() {
  const gr = useWidgetGroesse();
  const { setOpenProfilId } = useSchueler();
  const g = useLader<{ count: number; beispiele: { schueler_id: number; schueler_name: string; klasse_name: string }[] }>('/api/eltern/unvollstaendig', 60_000);
  const n = zeilen(gr);
  return (
    <MitDaten g={g}>
      {({ count, beispiele }) => count === 0 ? <Leer icon="family_restroom" text="Alle Eltern-Einträge haben eine Rolle." /> : (
        <div className="ld-wspalte">
          <p className="ld-wtext">Bei <b>{count}</b> Eltern-{count === 1 ? 'Eintrag' : 'Einträgen'} fehlt die Rolle (Mutter, Vater, …).</p>
          <ul className="ld-wl">
            {beispiele.slice(0, n).map(b => (
              <li key={b.schueler_id}>
                <button type="button" className="ld-wzeile ld-wzeile--kompakt" onClick={() => { setOpenProfilId(b.schueler_id, 'eltern'); globalNav('schueler'); }}>
                  <span className="ld-wzeile__text"><b>{b.schueler_name}</b><small>{b.klasse_name} · Rolle ergänzen</small></span>
                  <MIcon name="chevron_right" size={18} className="ld-wzeile__pfeil" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </MitDaten>
  );
}

// ── Wiederkehrend fehlend (Klassenbuch-Muster) ──────────────────────────────
export function MusterWidget() {
  const gr = useWidgetGroesse();
  const { setOpenProfilId } = useSchueler();
  const g = useLader<{ auffaellig: { id: number; name: string; klasse_name: string; anzahl: number; text: string | null }[] }>('/api/dashboard/klassenbuch-muster');
  const n = zeilen(gr);
  return (
    <MitDaten g={g}>
      {({ auffaellig }) => !auffaellig.length ? <Leer icon="event_available" text="Keine auffälligen Fehlzeiten im Klassenbuch der letzten drei Wochen." /> : (
        <ul className="ld-wl">
          {auffaellig.slice(0, n).map(s => (
            <li key={s.id}>
              <button type="button" className="ld-wzeile ld-wzeile--kompakt" onClick={() => { setOpenProfilId(s.id, 'fehlzeiten'); globalNav('schueler'); }}>
                <span className="ld-punkt ld-punkt--violett" aria-hidden="true" />
                <span className="ld-wzeile__text"><b>{s.name}</b><small>{s.text ? s.text[0].toUpperCase() + s.text.slice(1) : `${s.anzahl}× gefehlt in 3 Wochen`} · {s.klasse_name}</small></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </MitDaten>
  );
}

// ── Klassenbuch-Aktivität der Kolleg:innen ──────────────────────────────────
type Aktiv = { lehrer_vorname: string; lehrer_nachname: string; klasse_name: string; fach: string; stunde: number; datum?: string; klasse_id?: number; thema?: string | null };
export function AktivitaetWidget() {
  const gr = useWidgetGroesse();
  const sprung = useKlassenbuchSprung();
  const g = useLader<Aktiv[]>('/api/klassenbuch/aktivitaet');
  const n = zeilen(gr);
  return (
    <MitDaten g={g}>
      {(liste) => !liste.length ? <Leer icon="history_edu" text="Heute noch keine Einträge von Kolleg:innen." /> : (
        <ul className="ld-wl">
          {liste.slice(0, n).map((a, i) => (
            <li key={i}>
              <button type="button" className="ld-wzeile ld-wzeile--kompakt"
                onClick={() => a.datum ? sprung(a.datum, a.stunde, a.klasse_id) : globalNav('klassenbuch')}>
                <span className="ld-kuerzel ist-tint">{a.lehrer_vorname[0]}{a.lehrer_nachname[0]}</span>
                <span className="ld-wzeile__text"><b>{a.klasse_name} {a.fach} · {a.stunde}. Std</b><small>{a.lehrer_vorname[0]}. {a.lehrer_nachname}{a.thema ? ` · ${a.thema}` : ''}</small></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </MitDaten>
  );
}

// ── Ausstehende Aktionen ────────────────────────────────────────────────────
export function AktionenWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<Aktion[]>('/api/aktionen/faellig');
  const n = zeilen(gr);
  return (
    <MitDaten g={g}>
      {(liste) => !liste.length ? <Leer icon="checklist" text="Keine Aktionen fällig." aktion={{ label: 'Aktion anlegen', ziel: () => oeffneKlassenAktionen(null) }} /> : (
        <div className="ld-wspalte">
          <ul className="ld-wl">
            {liste.slice(0, n).map(a => (
              <li key={a.id}>
                <button type="button" className="ld-wzeile ld-wzeile--kompakt" onClick={() => oeffneKlassenAktionen(a.klasse_id, a.id)}>
                  <span className="ld-wzeile__text"><b>{a.titel}</b><small>{a.klasse_name} · fällig {ttmm(a.faellig_am)} · {a.erledigt_anzahl}/{a.gesamt_anzahl}</small></span>
                  <MIcon name="chevron_right" size={18} className="ld-wzeile__pfeil" />
                </button>
              </li>
            ))}
          </ul>
          <Mehr n={liste.length} gezeigt={n} label="Alle {n} Aktionen" ziel={() => oeffneKlassenAktionen(liste[0].klasse_id)} />
        </div>
      )}
    </MitDaten>
  );
}
